import type { ModalFuncProps } from 'antd';
import type { ModalFuncWithPromise } from 'antd/es/modal/useModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import BaseConfirm from './BaseConfirm';

interface Confirmation {
  id: number;
  config: ModalFuncProps;
  open: boolean;
  finish: (confirmed: boolean) => void;
}

/** 命令式确认统一进入 BaseConfirm，保留多实例、更新、销毁与 await 结果。 */
export default function useBaseConfirm() {
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const sequence = useRef(0);
  const pending = useRef(new Map<number, (confirmed: boolean) => void>());

  useEffect(() => {
    const callbacks = pending.current;
    return () => {
      callbacks.forEach((resolve) => resolve(false));
      callbacks.clear();
    };
  }, []);

  const confirm: ModalFuncWithPromise = useCallback(
    (config: ModalFuncProps) => {
      const id = ++sequence.current;
      const result = new Promise<boolean>((resolve) => {
        pending.current.set(id, resolve);
      });
      const finish = (confirmed: boolean) => {
        const resolve = pending.current.get(id);
        if (!resolve) return;
        pending.current.delete(id);
        resolve(confirmed);
        setConfirmations((items) =>
          items.map((item) =>
            item.id === id ? { ...item, open: false } : item,
          ),
        );
      };
      setConfirmations((items) => [
        ...items,
        { id, config, open: config.open ?? true, finish },
      ]);
      return {
        destroy: () => finish(false),
        update: (update) => {
          if (!pending.current.has(id)) return;
          return setConfirmations((items) =>
            items.map((item) => {
              if (item.id !== id) return item;
              const next =
                typeof update === 'function' ? update(item.config) : update;
              return {
                ...item,
                config: { ...item.config, ...next },
                open: next.open ?? item.open,
              };
            }),
          );
        },
        then: (resolve, _reject) => result.then(resolve),
      };
    },
    [],
  );

  const holder = (
    <>
      {confirmations.map(({ id, config, open, finish }) => (
        <BaseConfirm
          key={id}
          config={config}
          open={open}
          onFinish={finish}
          onHidden={() => {
            setConfirmations((items) => items.filter((item) => item.id !== id));
            config.afterClose?.();
          }}
        />
      ))}
    </>
  );
  return [confirm, holder] as const;
}
